import { GetServerSideProps } from 'next';
import Head from 'next/head';
import { Collection, FAQPage, Thing, WithContext } from 'schema-dts';

import { PageSchemaType } from '@/lib/types';

import HygraphRichText from '@/components/hygraph-rich-text';
import DefaultLayout from '@/components/layout/default-layout';
import RelatedLinks from '@/components/related-links';
import Row from '@/components/row';
import { Typography, TypographyColor } from '@/components/typography';

import { GlobalContextProvider } from '@/context/global-context';
import { RowLayout } from '@/enums/row-layout';
import JobCategoryBreadcrumb from '@/features/job-category-page/components/job-category-breadcrumb';
import JobCategoryJobsList from '@/features/job-category-page/components/job-category-jobs-list';
import breadcrumbPropsFromJobCategoryPage from '@/features/job-category-page/mappers/job-category-breadcrumb.mapper';
import JobCategoryPageService from '@/features/job-category-page/services/page.service';
import JobCategoryPageJobsService, {
  GetJobCategoryPageJobsResponse,
} from '@/features/job-category-page/services/public-jobs.service';
import JobCategoryPageSidebarService, {
  SidebarServiceGetResponse,
} from '@/features/job-category-page/services/sidebar.service';
import { JobCategoryPage } from '@/features/job-category-page/types';
import { getRenderer } from '@/hygraph/renderers';
import { TNavbarItem } from '@/services/navigation.service';
import NavigationService from '@/services/navigation.service';
import type { TBreadcrumb } from '@/services/schema-service';
import SchemaService, { nomadHealthOrg } from '@/services/schema-service';

type HealthCareJobsPageProps = {
  canonicalLink: string | null;
  page: JobCategoryPage;
  schema?: WithContext<Thing>[];
  sidebar: SidebarServiceGetResponse;
  breadcrumbs: TBreadcrumb[];
  navbar: TNavbarItem[];
} & GetJobCategoryPageJobsResponse;

export default function HealthCareJobsPage({
  canonicalLink,
  jobs,
  relatedJobs,
  relatedJobsLevel,
  page,
  sidebar,
  schema,
  breadcrumbs,
  navbar,
}: HealthCareJobsPageProps) {
  return (
    <GlobalContextProvider
      pageMeta={page.seo}
      schema={schema}
      pageStaticData={{ navbarNavigation: navbar }}
    >
      {canonicalLink && (
        <Head>
          <link rel='canonical' href={canonicalLink} />
        </Head>
      )}

      <DefaultLayout>
        <Row layout={RowLayout.NARROW_COLUMN_CENTER} className='mt-17 mb-32'>
          <JobCategoryBreadcrumb items={breadcrumbs} />
          <Typography.Heading1 as='h1' className='mb-8'>
            {page.title}
          </Typography.Heading1>
          {page.contents.top && (
            <div className='mb-8'>
              <HygraphRichText
                {...page.contents.top}
                color={TypographyColor.DEFAULT}
              />
            </div>
          )}
          <Row
            layout={RowLayout.ONE_COLUMN_WITH_SIDEBAR_REVERSE}
            className='my-8 lg:px-0'
          >
            <JobCategoryJobsList
              id={page.id}
              jobs={jobs}
              relatedJobs={relatedJobs}
              relatedJobsLevel={relatedJobsLevel}
              page={page}
            />
            <RelatedLinks {...sidebar} />
          </Row>
          {page.contents.bottom.map((bottomItem) => {
            const BottomRenderer = getRenderer(bottomItem?.__typename);
            if (!BottomRenderer) return null;

            return <BottomRenderer {...bottomItem} key={bottomItem.id} />;
          })}
        </Row>
      </DefaultLayout>
    </GlobalContextProvider>
  );
}

const ONE_HOUR_IN_SECONDS = 60 * 60 - 1;
const ONE_DAY_IN_SECONDS = 60 * 60 * 24 - 1;

export const getServerSideProps: GetServerSideProps<
  HealthCareJobsPageProps
> = async (context) => {
  context.res.setHeader(
    'Cache-Control',
    `public, s-maxage=${ONE_HOUR_IN_SECONDS}, stale-while-revalidate=${ONE_DAY_IN_SECONDS}`
  );

  const slug = context.params?.slug || '';
  const slugs = Array.isArray(slug) ? slug : [slug];

  const pageData = await JobCategoryPageService.getPageData(
    slugs,
    context.preview
  );

  if (!pageData) return { notFound: true };

  const { canonicalLink, page } = pageData;

  const [sidebar, jobsSearchResponse, navbar] = await Promise.all([
    JobCategoryPageSidebarService.get(page),
    JobCategoryPageJobsService.get(page),
    NavigationService.getDefault(context.preview),
  ]);

  const [schema, breadcrumbs] = buildPageSchemaAndBreadcrumbs(page);

  return {
    props: {
      page,
      sidebar,
      canonicalLink,
      breadcrumbs,
      schema,
      navbar,
      ...jobsSearchResponse,
    },
  };
};

function buildPageSchemaAndBreadcrumbs(
  page: JobCategoryPage
): [WithContext<Thing>[], TBreadcrumb[]] {
  if (page.seo?.noIndex) return [[], []]; // Don't bother building schema if page is no-index

  const isCollection = // Collection is default
    !page.seo?.schemaType || page.seo.schemaType === PageSchemaType.Collection;
  const isFaqPage = page.seo?.schemaType === PageSchemaType.FaqPage;

  const { images, questions, things } = SchemaService.buildSchemaData(
    page.contents.bottom || [],
    { specialty: page.specialty || undefined }
  );

  const pageSchema = SchemaService.buildPageThing(page, images);
  pageSchema.mainEntity = isFaqPage ? questions : [];

  let collectionParts: Collection['hasPart'] = [pageSchema];

  if (!isFaqPage && questions.length > 0) {
    const faqPageThing: FAQPage = { '@type': 'FAQPage', mainEntity: questions };
    if (isCollection) {
      collectionParts = collectionParts.concat([faqPageThing]);
    } else {
      things.unshift({ '@context': 'https://schema.org', ...faqPageThing });
    }
  }

  const breadcrumbs = breadcrumbPropsFromJobCategoryPage(page);
  if (breadcrumbs.length > 0) {
    things.unshift(SchemaService.buildBreadcrumbsList(breadcrumbs));
  }

  if (!isCollection) {
    return [
      [{ '@context': 'https://schema.org', ...pageSchema }, ...things],
      breadcrumbs,
    ];
  }

  const collectionThing: WithContext<Collection> = {
    '@context': 'https://schema.org',
    '@type': 'Collection',
    publisher: nomadHealthOrg,
    about: nomadHealthOrg,
    author: nomadHealthOrg,
    creator: nomadHealthOrg,
    hasPart: collectionParts,
  };
  return [[collectionThing, ...things], breadcrumbs];
}
